/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * Wires the optional prev/next buttons to scroll the track by roughly one page and toggles
 * their `disabled` state at the start/end of the scroll range. The track is a native
 * scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus all work
 * without JS.
 *
 * Optional auto-rotation: when the host has `data-autoplay="<ms>"`, the track advances
 * `data-autoplay-step` items at a time and loops back to the start. It pauses while hovered, focused, touched,
 * off screen, or in a hidden tab, never runs under prefers-reduced-motion, and the
 * optional `[data-carousel-toggle]` button stops/starts it.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel [data-autoplay="4000"]>
 *     [<button data-carousel-toggle>] [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");
    this.toggleButton = this.querySelector("[data-carousel-toggle]");

    if (!this.track) return;

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.prevButton?.addEventListener("click", () => this.scrollByPage(-1), { signal });
    this.nextButton?.addEventListener("click", () => this.scrollByPage(1), { signal });
    this.track.addEventListener("scroll", () => this.updateControls(), { passive: true, signal });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
    this.setupAutoplay(signal);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.stopTimer();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction * this.inlineDirection();
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** 1 for left-to-right pages, -1 for right-to-left, where scrollLeft runs negative. */
  inlineDirection() {
    return getComputedStyle(this.track).direction === "rtl" ? -1 : 1;
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const atStart = position <= 1;
    const atEnd = position >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }

  /** @param {AbortSignal} signal */
  setupAutoplay(signal) {
    this.interval = Number(this.dataset.autoplay) || 0;
    if (!this.interval) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.stoppedByUser = reducedMotion.matches;
    this.hovered = false;
    this.focused = false;
    this.visible = false;
    this.syncToggle();

    this.addEventListener("pointerenter", () => { this.hovered = true; this.refreshTimer(); }, { signal });
    this.addEventListener("pointerleave", () => { this.hovered = false; this.refreshTimer(); }, { signal });
    this.addEventListener("focusin", () => { this.focused = true; this.refreshTimer(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (this.contains(/** @type {Node} */ (event.relatedTarget))) return;
      this.focused = false;
      this.refreshTimer();
    }, { signal });
    // A touch or drag means the shopper is browsing by hand; stop rotating for this visit.
    this.track.addEventListener("pointerdown", () => this.setStopped(true), { signal });
    document.addEventListener("visibilitychange", () => this.refreshTimer(), { signal });

    this.toggleButton?.addEventListener("click", () => this.setStopped(!this.stoppedByUser), { signal });

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.refreshTimer();
    }, { threshold: 0.3 });
    this.intersectionObserver.observe(this);
  }

  /** @param {boolean} stopped */
  setStopped(stopped) {
    this.stoppedByUser = stopped;
    this.syncToggle();
    this.refreshTimer();
  }

  syncToggle() {
    if (!this.toggleButton) return;
    const { labelPause, labelPlay } = this.toggleButton.dataset;
    this.toggleButton.setAttribute("aria-label", (this.stoppedByUser ? labelPlay : labelPause) || "");
    this.toggleButton.querySelector("[data-icon-pause]")?.classList.toggle("hidden", this.stoppedByUser);
    this.toggleButton.querySelector("[data-icon-play]")?.classList.toggle("hidden", !this.stoppedByUser);
  }

  refreshTimer() {
    const shouldRun = !this.stoppedByUser && !this.hovered && !this.focused && this.visible && !document.hidden;
    if (shouldRun && !this.timer) {
      this.timer = window.setInterval(() => this.advance(), this.interval);
    } else if (!shouldRun) {
      this.stopTimer();
    }
  }

  stopTimer() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  /** Move forward by one item, or loop back to the start once the end is reached. */
  advance() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 1) return;

    const direction = this.inlineDirection();
    if (Math.abs(this.track.scrollLeft) >= maxScroll - 1) {
      this.track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    const firstItem = /** @type {HTMLElement | null} */ (this.track.firstElementChild);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const itemsPerMove = Math.max(1, Number(this.dataset.autoplayStep) || 1);
    const step = ((firstItem?.getBoundingClientRect().width || this.track.clientWidth) + gap) * itemsPerMove;
    this.track.scrollBy({ left: step * direction, behavior: "smooth" });
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
